import React, { useState, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Modal, FilledInput, Typography, Button, FormGroup, FormLabel, FormControl, Box, Grid, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import dayjs from 'dayjs';

import DateRangeModal from '../../common/date-range';
import Type from './type';

import themeColors from '../../assets/colors';
import { get, post } from '../../utils/api-services';
import helpers from '../../utils/helpers';
import AuthContext from '../../context';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: themeColors.background.default,
        padding: 20,
        width: '80%',
        borderRadius: 10
    },
    store: {
        marginLeft: 15,
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none',
        }
    },
    bottom: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    types: {
        display: 'flex',
        flexDirection: 'column'
    }
}));

const SalePriceCreate = ({ open, onClose, products }) => {

    const classes = useStyles();
    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const [state, setState] = useState({
        prices: {
            startDate: dayjs().format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            dateRange: `${dayjs().format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`,
            product: products[0],
            types: []
        },
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        }
    });

    const handleModal = () => setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));

    const handleAutocomplete = value => setState(prev => ({ ...prev, prices: { ...prev.prices, product: value } }));

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            prices: {
                ...prev.prices,
                dateRange,
                startDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                endDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const getTypes = async id => {
        showLoader(true);
        const response = await get(`/product/${id}`);
        if (response.status === 200) {
            const { data } = response;
            setState(prev => ({ ...prev, prices: { ...prev.prices, types: data.types } }));
        } else {
            setState(prev => ({ ...prev, prices: { ...prev.prices, types: [] } }));
            console.log('[SalePriceCreate] getTypes error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChangeSelect = (e) => {
        const { target } = e;
        const array = state.prices.types.slice();

        array.map(item => {
            if (`${item.id}` === target.name) {
                item.selected = target.checked;
                if (!target.checked) {
                    item.price = 0;
                }
            }
        });

        setState(prev => ({ ...prev, prices: { ...prev.prices, types: array } }));
    }

    const handleChangePrice = e => {
        const { target } = e;
        const array = state.prices.types.slice();

        array.map(item => {
            if (`${item.id}` === target.name) {
                item.price = target.value;
            }
        });

        setState(prev => ({ ...prev, prices: { ...prev.prices, types: array } }));
    }

    const validateForm = () => {
        const { product } = state.prices;

        if (product.id) {
            const cond = window.confirm('Confirm if all data is correct');
            if (cond) {
                store();
            }
        } else {
            showToast('Select some product', 'error');
        }
    }

    const store = async () => {
        showLoader(true);
        const response = await post('/ticketSalePrice/store', state.prices);
        if (response.status === 200) {
            onClose(true);
            showToast(response.message);
        } else {
            console.log('[SalePriceCreate] store error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.root}
        >
            <div className={classes.container}>
                <Typography variant='h2'>{'Create ticket sale prices'}</Typography>
                <Grid container>
                    <Grid item xs={12} lg={4}>
                        <FormGroup onClick={handleModal}>
                            <FormLabel>{'Date range'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    readOnly
                                    type='text'
                                    placeholder='Date range'
                                    value={state.prices.dateRange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <FormGroup>
                            <FormLabel>{'Product'}</FormLabel>
                            <Autocomplete
                                options={products}
                                value={state.prices.product}
                                getOptionLabel={option => option?.name ?? ''}
                                getOptionSelected={(option, value) => option.id === value.id}
                                onChange={(e, value) => {
                                    handleAutocomplete(value);
                                    (Number(value?.id) !== 0)
                                        ? getTypes(value.id)
                                        : setState(prev => ({ ...prev, prices: { ...prev.prices, types: [] } }));
                                }}
                                renderInput={params => <TextField {...params} placeholder='Product' />}
                            />
                        </FormGroup>
                    </Grid>
                </Grid>
                <div className={classes.bottom}>
                    <div className={classes.types}>
                        {
                            state.prices.types.length ?
                                state.prices.types.map((item, i) => {
                                    return (
                                        <Type
                                            key={item.id}
                                            item={item}
                                            onChangeSelect={handleChangeSelect}
                                            onChangePrice={handleChangePrice}
                                        />
                                    )
                                }) :
                                <Typography variant='inherit'>{'The product has no types'}</Typography>
                        }
                    </div>
                    <div>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={onClose}
                        >
                            {'Cancel'}
                        </Button>
                        <Button
                            variant='contained'
                            className={classes.store}
                            onClick={validateForm}
                        >
                            {'Store'}
                        </Button>
                    </div>
                </div>

                <DateRangeModal
                    open={state.rangeModal.open}
                    onClose={handleModal}
                    range={state.rangeModal.range}
                    onChange={handleDateChange}
                />
            </div>
        </Modal>
    );
}

export default SalePriceCreate;